import classNames from "classnames";
import React, { useState } from "react";
import { replaceWithUnicode } from "../shared/string";
import { TabProps } from "./Tab";

// An attempt to restrict the types of children available to this component to
// React components with props type 'TabProps'.
// Unfortunately this is not possible with the typescript types available for React
// today, trying to put any other React component as a child to Tabs will not throw
// any typescript errors. It at least restricts so that strings are not allowed as
// direct children.
type Props = {
  children?: React.ReactElement<TabProps> | React.ReactElement<TabProps>[];
};

const Tabs = (props: Props) => {
  const { children: tabs } = props;

  const [activeTab, setActiveTab] = useState(
    Array.isArray(tabs) ? tabs[0].props.id : tabs?.props.id
  );

  const activeTabClassNames = "text-brand-primary-30 border-b-3 border-brand-primary-30";
  const inactiveTabClassNames =
    "text-brand-primary-dim hover:border-b-3 hover:border-brand-primary-95";

  const renderTabHeader = (tab: React.ReactElement<TabProps>, index?: number) => (
    <li key={index} className="px-4 pt-2 lg:pt-4 mb-10 lg:mb-16">
      <div
        className={classNames(
          "text-lg pb-2 lg:pb-4 cursor-pointer font-bold hover:text-brand-primary-30 focus:shadow-outline",
          {
            [activeTabClassNames]: activeTab === tab.props.id,
            [inactiveTabClassNames]: activeTab !== tab.props.id
          }
        )}
        onClick={() => setActiveTab(tab.props.id)}
      >
        {replaceWithUnicode(tab.props.title)}
      </div>
    </li>
  );

  const renderTabHeaders = () => {
    const tabHeaders: JSX.Element[] = [];

    if (Array.isArray(tabs)) {
      tabs.forEach((tab, index) => {
        tabHeaders.push(renderTabHeader(tab, index));
      });
    } else if (tabs) {
      tabHeaders.push(renderTabHeader(tabs));
    }

    return tabHeaders;
  };

  const renderActiveTab = () => {
    if (Array.isArray(tabs)) {
      return tabs.find(tab => tab.props.id === activeTab);
    } else {
      return tabs;
    }
  };

  return (
    <>
      <ul className="flex justify-between">{renderTabHeaders()}</ul>
      {renderActiveTab()}
    </>
  );
};

export default Tabs;
